import { coreFetch, HttpMethod, MIME_TYPE } from '@libs/core';
import urlJoin from 'url-join';
import type {
    ApiProxyClient,
    ApiProxyClientOptions,
    ApiProxyClientRequestBuilder,
} from '../types';

export function createApiProxyClient(
    basePath: string,
    requestBuilder: ApiProxyClientRequestBuilder = options => options
): ApiProxyClient {
    async function handleRequest<ReturnValue, TBody = BodyInit>(
        method: HttpMethod,
        pathname: string,
        options: Omit<ApiProxyClientOptions<TBody>, 'method'>
    ) {
        // @ts-expect-error -- I don't know how to fix this :)
        const { body, ...builtOptions } = requestBuilder({
            method,
            ...options,
        });
        const defaultContentTypeHeader: HeadersInit =
            body instanceof FormData ? {} : { 'Content-Type': MIME_TYPE.json };
        const parsedBody =
            body instanceof FormData ? body : JSON.stringify(body);

        const combinedOptions = {
            cache: 'no-store' as const,
            ...builtOptions,
            body: parsedBody,
            headers: {
                ...defaultContentTypeHeader,
                ...builtOptions.headers,
            },
            method,
        };

        const { zodSchema, numberOfRetries, timeoutDuration, ...init } =
            combinedOptions;

        // Supporting only strings for simplicity. Can be extended if needed
        const inputString = urlJoin(basePath, pathname);
        return await coreFetch<ReturnValue>(
            inputString,
            init,
            zodSchema,
            numberOfRetries,
            timeoutDuration
        );
    }

    const createRequestHandler =
        (method: HttpMethod) =>
        async <ReturnValue, TBody>(
            pathname: string,
            options: ApiProxyClientOptions<TBody>
        ) =>
            handleRequest<ReturnValue, TBody>(method, pathname, options);

    return {
        get: createRequestHandler(HttpMethod.GET),
        post: createRequestHandler(HttpMethod.POST),
        put: createRequestHandler(HttpMethod.PUT),
        patch: createRequestHandler(HttpMethod.PATCH),
        options: createRequestHandler(HttpMethod.OPTIONS),
        delete: createRequestHandler(HttpMethod.DELETE),
        request: handleRequest,
    };
}
